import { useEffect, useMemo, useState, type DragEvent } from 'react';
import {
  publishAvatarFactoryDraft,
  restoreAvatarFactoryVersion,
  saveAvatarFactoryVersion,
  updateAvatarFactoryDraft,
  uploadAvatarFactorySource,
  type AvatarFactoryDraftPatch,
} from '../../lib/admin-avatar-factory-api';
import { SOURCE_STAGE_KEYS, type SourceStageKey } from './i18n';
import type { AutosaveState, AvatarFactoryChildProps } from './types';

const AUTOSAVE_DEBOUNCE_MS = 700;
const DEFAULT_DAILY_TIME = '09:00';
const MAX_SOURCE_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;
const MAX_SOURCE_UPLOAD_SIZE_LABEL = '1GB';
const TASK_TYPE_VALUES = ['send_message', 'edit_message', 'daily_digest'] as const;

type TaskTypeValue = (typeof TASK_TYPE_VALUES)[number];

function toText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function getDailyTime(scheduleConfig: Record<string, unknown>): string {
  const time = toText(scheduleConfig.time);
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }

  const cronParts = toText(scheduleConfig.cron_expr).trim().split(/\s+/);
  if (cronParts.length >= 2) {
    const minute = Number(cronParts[0]);
    const hour = Number(cronParts[1]);
    if (Number.isInteger(minute) && Number.isInteger(hour) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
  }

  return DEFAULT_DAILY_TIME;
}

function toDailyCron(time: string): string {
  const [hour = '9', minute = '0'] = time.split(':');
  return `${Number(minute)} ${Number(hour)} * * *`;
}

function getTaskType(scheduleConfig: Record<string, unknown>): TaskTypeValue {
  const taskType = toText(scheduleConfig.task_type);
  return TASK_TYPE_VALUES.includes(taskType as TaskTypeValue) ? (taskType as TaskTypeValue) : 'send_message';
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function mergePatch(previous: AvatarFactoryDraftPatch | null, next: AvatarFactoryDraftPatch): AvatarFactoryDraftPatch {
  if (!previous) {
    return next;
  }

  return {
    ...previous,
    ...next,
    scheduleConfigJson: next.scheduleConfigJson ?? previous.scheduleConfigJson,
  };
}

export default function SourceSetupTab({ accessToken, copy, draft, onDraftChange, onError }: AvatarFactoryChildProps) {
  const [isProcessingSource, setIsProcessingSource] = useState(false);
  const [isUploadDragging, setIsUploadDragging] = useState(false);
  const [hasProcessedSource, setHasProcessedSource] = useState(draft.sources.length > 0);
  const [activeStage, setActiveStage] = useState<SourceStageKey>('ready');
  const [agentName, setAgentName] = useState(draft.displayName);
  const [greeting, setGreeting] = useState(draft.greeting);
  const [scheduleConfig, setScheduleConfig] = useState<Record<string, unknown>>(draft.scheduleConfigJson);
  const [autosaveState, setAutosaveState] = useState<AutosaveState>('idle');
  const [pendingPatch, setPendingPatch] = useState<AvatarFactoryDraftPatch | null>(null);
  const [versionName, setVersionName] = useState('');
  const [versionMessage, setVersionMessage] = useState('');
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [setWechatEntrypoint, setSetWechatEntrypoint] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');

  useEffect(() => {
    setAgentName(draft.displayName);
    setGreeting(draft.greeting);
    setScheduleConfig(draft.scheduleConfigJson);
    setHasProcessedSource(draft.sources.length > 0);
  }, [draft.displayName, draft.greeting, draft.id, draft.scheduleConfigJson, draft.sources.length]);

  useEffect(() => {
    if (!pendingPatch) {
      return;
    }

    setAutosaveState('saving');
    let cancelled = false;

    const timer = window.setTimeout(() => {
      void updateAvatarFactoryDraft(accessToken, draft.id, pendingPatch)
        .then((nextDraft) => {
          if (cancelled) {
            return;
          }
          onDraftChange(nextDraft);
          setAutosaveState('saved');
          setPendingPatch(null);
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          setAutosaveState('failed');
          setPendingPatch(null);
          onError(getErrorMessage(error, copy.source.requestFallback));
        });
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [accessToken, copy.source.requestFallback, draft.id, onDraftChange, onError, pendingPatch]);

  const firstSourceMarkdown = draft.sources[0]?.markdownText ?? '';

  const scheduleLabel = useMemo(() => toText(scheduleConfig.label), [scheduleConfig.label]);
  const scheduleTime = useMemo(() => getDailyTime(scheduleConfig), [scheduleConfig]);
  const scheduleTaskType = useMemo(() => getTaskType(scheduleConfig), [scheduleConfig]);
  const scheduleMessageContent = useMemo(
    () => toText(scheduleConfig.message_content) || toText(scheduleConfig.prompt),
    [scheduleConfig.message_content, scheduleConfig.prompt],
  );

  const queuePatch = (patch: AvatarFactoryDraftPatch) => {
    setPendingPatch((previous) => mergePatch(previous, patch));
  };

  const handleSaveNamedVersion = async () => {
    const trimmedName = versionName.trim();
    if (!trimmedName || isSavingVersion) {
      return;
    }

    setIsSavingVersion(true);
    setVersionMessage('');

    try {
      const nextDraft = await saveAvatarFactoryVersion(accessToken, draft.id, trimmedName);
      onDraftChange(nextDraft);
      setVersionName('');
      setVersionMessage(copy.common.versionSaved);
    } catch (error) {
      onError(getErrorMessage(error, copy.source.requestFallback));
    } finally {
      setIsSavingVersion(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    setVersionMessage('');

    try {
      const nextDraft = await restoreAvatarFactoryVersion(accessToken, draft.id, versionId);
      onDraftChange(nextDraft);
      setVersionMessage(copy.common.versionRestored);
    } catch (error) {
      onError(getErrorMessage(error, copy.source.requestFallback));
    }
  };

  const handleSourceUpload = async (input: { files: File[] }) => {
    setIsProcessingSource(true);
    setActiveStage('accepted');
    setPublishMessage('');

    try {
      const nextDraft = await uploadAvatarFactorySource(accessToken, draft.id, input);
      onDraftChange(nextDraft);
      setHasProcessedSource(true);
      setActiveStage('ready');
    } catch (error) {
      onError(getErrorMessage(error, copy.source.requestFallback));
    } finally {
      setIsProcessingSource(false);
    }
  };

  const uploadSelectedFiles = (selectedFiles: FileList | File[]) => {
    const files = Array.from(selectedFiles);
    if (files.length === 0) {
      return;
    }

    const selectedSizeBytes = files.reduce((total, file) => total + file.size, 0);
    if (selectedSizeBytes > MAX_SOURCE_UPLOAD_SIZE_BYTES) {
      onError(copy.source.uploadSizeExceeded(MAX_SOURCE_UPLOAD_SIZE_LABEL));
      return;
    }

    void handleSourceUpload({ files });
  };

  const handleUploadDrag = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsUploadDragging(true);
  };

  const handleUploadDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsUploadDragging(false);
    uploadSelectedFiles(event.dataTransfer.files);
  };

  const handlePublish = async () => {
    if (!hasProcessedSource || isPublishing) {
      return;
    }

    setIsPublishing(true);
    setPublishMessage('');

    try {
      const result = await publishAvatarFactoryDraft(accessToken, draft.id, {
        setWechatEntrypoint,
      });
      setPublishMessage(copy.source.publishResult(result.profileId));
    } catch (error) {
      onError(getErrorMessage(error, copy.source.publishFallback));
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="avatar-factory-source-panel">
      <div className="avatar-factory-section-heading">
        <p className="avatar-factory-panel-kicker">{copy.source.input}</p>
        <h2>{copy.source.title}</h2>
        <span>
          {autosaveState === 'saving'
            ? copy.common.autosaveSaving
            : autosaveState === 'saved'
              ? copy.common.autosaveSaved
              : copy.source.draftOnly}
        </span>
      </div>

      <div className="avatar-factory-source-grid">
        <label
          className={`avatar-factory-upload-card${isUploadDragging ? ' is-dragging' : ''}`}
          onDragEnter={handleUploadDrag}
          onDragOver={handleUploadDrag}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsUploadDragging(false);
          }}
          onDrop={handleUploadDrop}
        >
          <span className="avatar-factory-upload-glyph" aria-hidden="true" />
          <strong>{copy.source.uploadSourceFile}</strong>
          <small>{copy.source.uploadHint}</small>
          <em>{copy.source.uploadBrowseHint}</em>
          <input
            aria-label={copy.source.uploadSourceFile}
            type="file"
            multiple
            accept=".txt,.md,.pdf,.docx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(event) => {
              uploadSelectedFiles(event.target.files ?? []);
              event.currentTarget.value = '';
            }}
          />
        </label>
      </div>

      <div className="avatar-factory-pipeline-card" aria-label={copy.source.processingStagesAria}>
        <div className="avatar-factory-card-heading">
          <h3>{copy.source.pipeline}</h3>
          <span>{isProcessingSource ? copy.common.processing : hasProcessedSource ? copy.common.ready : copy.common.waiting}</span>
        </div>
        <ol>
          {SOURCE_STAGE_KEYS.map((stage) => {
            let state = 'pending';
            if (stage === activeStage) {
              state = 'active';
            }
            if (!isProcessingSource && hasProcessedSource && stage === 'ready') {
              state = 'active';
            }
            if (!isProcessingSource && hasProcessedSource && stage !== 'ready') {
              state = 'complete';
            }
            return (
              <li key={stage} data-state={state} aria-current={stage === activeStage ? 'step' : undefined}>
                {copy.source.stages[stage]}
              </li>
            );
          })}
        </ol>
      </div>

      <div className="avatar-factory-card avatar-factory-publish-card">
        <div className="avatar-factory-card-heading">
          <h3>{copy.source.publishTitle}</h3>
          <span>{publishMessage ? copy.source.publishReady : hasProcessedSource ? copy.common.ready : copy.common.waiting}</span>
        </div>
        <p>{copy.source.publishDescription}</p>
        <label className="avatar-factory-checkbox-row" htmlFor="factory-publish-wechat">
          <input
            id="factory-publish-wechat"
            aria-label={copy.source.publishWithWechat}
            type="checkbox"
            checked={setWechatEntrypoint}
            onChange={(event) => setSetWechatEntrypoint(event.target.checked)}
          />
          <span>{copy.source.publishWithWechat}</span>
        </label>
        <button
          type="button"
          disabled={!hasProcessedSource || isPublishing}
          onClick={() => {
            void handlePublish();
          }}
        >
          {isPublishing ? copy.source.publishing : copy.source.publishAction}
        </button>
        {publishMessage ? <p role="status">{publishMessage}</p> : null}
      </div>

      <div className="avatar-factory-card avatar-factory-markdown-card">
        <div className="avatar-factory-card-heading">
          <h3>{copy.source.markdownPreview}</h3>
          <span>{firstSourceMarkdown ? copy.source.converted : copy.common.empty}</span>
        </div>
        <pre>{firstSourceMarkdown}</pre>
      </div>

      <div className="avatar-factory-form-grid">
        <div className="avatar-factory-field avatar-factory-field-wide">
          <label htmlFor="factory-agent-name">{copy.source.agentName}</label>
          <input
            id="factory-agent-name"
            aria-label={copy.source.agentName}
            type="text"
            value={agentName}
            onChange={(event) => {
              const nextValue = event.target.value;
              setAgentName(nextValue);
              queuePatch({
                displayName: nextValue,
                greeting,
              });
            }}
          />
        </div>

        <div className="avatar-factory-field avatar-factory-field-wide">
          <label htmlFor="factory-greeting">{copy.source.greeting}</label>
          <textarea
            id="factory-greeting"
            aria-label={copy.source.greeting}
            value={greeting}
            rows={3}
            onChange={(event) => {
              const nextValue = event.target.value;
              setGreeting(nextValue);
              queuePatch({
                displayName: agentName,
                greeting: nextValue,
              });
            }}
          />
        </div>

        <div className="avatar-factory-field">
          <label htmlFor="factory-schedule-label">{copy.source.scheduleLabel}</label>
          <input
            id="factory-schedule-label"
            aria-label={copy.source.scheduleLabel}
            type="text"
            value={scheduleLabel}
            onChange={(event) => {
              const nextValue = event.target.value;
              setScheduleConfig((previous) => {
                const nextConfig = { ...previous, label: nextValue };
                queuePatch({ scheduleConfigJson: nextConfig });
                return nextConfig;
              });
            }}
          />
        </div>

        <div className="avatar-factory-field">
          <label htmlFor="factory-schedule-time">{copy.source.scheduleTime}</label>
          <input
            id="factory-schedule-time"
            aria-label={copy.source.scheduleTime}
            type="time"
            value={scheduleTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              setScheduleConfig((previous) => {
                const nextConfig = {
                  ...previous,
                  time: nextValue,
                  cron_expr: toDailyCron(nextValue),
                };
                queuePatch({ scheduleConfigJson: nextConfig });
                return nextConfig;
              });
            }}
          />
        </div>

        <div className="avatar-factory-field">
          <label htmlFor="factory-schedule-task-type">{copy.source.scheduleTaskType}</label>
          <select
            id="factory-schedule-task-type"
            aria-label={copy.source.scheduleTaskType}
            value={scheduleTaskType}
            onChange={(event) => {
              const nextValue = event.target.value as TaskTypeValue;
              setScheduleConfig((previous) => {
                const nextConfig = { ...previous, task_type: nextValue };
                queuePatch({ scheduleConfigJson: nextConfig });
                return nextConfig;
              });
            }}
          >
            <option value="send_message">{copy.source.taskTypes.sendMessage}</option>
            <option value="edit_message">{copy.source.taskTypes.editMessage}</option>
            <option value="daily_digest">{copy.source.taskTypes.dailyDigest}</option>
          </select>
        </div>

        <div className="avatar-factory-field avatar-factory-field-wide">
          <label htmlFor="factory-schedule-message-content">{copy.source.scheduleMessageContent}</label>
          <textarea
            id="factory-schedule-message-content"
            aria-label={copy.source.scheduleMessageContent}
            value={scheduleMessageContent}
            rows={4}
            onChange={(event) => {
              const nextValue = event.target.value;
              setScheduleConfig((previous) => {
                const nextConfig = {
                  ...previous,
                  message_content: nextValue,
                  prompt: nextValue,
                };
                queuePatch({ scheduleConfigJson: nextConfig });
                return nextConfig;
              });
            }}
          />
        </div>
      </div>

      <div className="avatar-factory-version-row">
        <label htmlFor="factory-version-name">{copy.common.versionName}</label>
        <input
          id="factory-version-name"
          aria-label={copy.common.versionName}
          type="text"
          value={versionName}
          onChange={(event) => setVersionName(event.target.value)}
        />
        <button
          type="button"
          disabled={!versionName.trim() || isSavingVersion}
          onClick={() => {
            void handleSaveNamedVersion();
          }}
        >
          {copy.common.saveNamedVersion}
        </button>
      </div>

      <div className="avatar-factory-card avatar-factory-versions-card">
        <div className="avatar-factory-card-heading">
          <h3>{copy.common.versions}</h3>
          <span>{copy.common.versionsSaved(draft.versions.length)}</span>
        </div>
        {draft.versions.length > 0 ? (
          <ul>
            {draft.versions.map((version) => (
              <li key={version.id}>
                <span>{version.name}</span>{' '}
                <button
                  type="button"
                  onClick={() => {
                    void handleRestoreVersion(version.id);
                  }}
                >
                  {copy.common.restoreVersion(version.name)}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>{copy.common.noSavedVersions}</p>
        )}
        {versionMessage ? <p>{versionMessage}</p> : null}
      </div>

      <p className="avatar-factory-autosave" aria-live="polite">
        {autosaveState === 'saving' ? copy.common.saving : null}
        {autosaveState === 'saved' ? copy.common.saved : null}
        {autosaveState === 'failed' ? copy.common.saveFailed : null}
      </p>
    </div>
  );
}
